import type { SupportedLanguage } from "../../interfaces";
import type { CongressCountdownCopy } from "../../components/ui/CongressCard";

/**
 * The countdown's own words — the day/hour/minute unit letters and the "live"
 * pill — resolved in the CONTENT language.
 *
 * ONE DOOR. `CongressCard` is a design-system primitive rendered in Storybook,
 * which mounts no i18n provider, so it cannot call `t()` itself: it takes the
 * resolved copy as a prop. Every congress surface resolves it here, so the axis
 * is stated once instead of at each card.
 *
 * `lng` is safe here — and unreliable in general. i18next loads locale bundles
 * lazily over HTTP, so `t(key, { lng })` for a language the reader is not
 * currently in resolves against a bundle that may not be in memory yet.
 * `setRoomContentLang` preloads the content-language bundle at the single write
 * site, which is what makes this door (and `resolveTopSessionsHeading`) resolve
 * rather than fall through to English. See `roomContentLangStore.ts`.
 *
 * ⚠️ ARABIC CARRIES ENGLISH VALUES ON PURPOSE — `congressCountdown.*` in
 * `public/locales/ar/translation.json` reads `d` / `h` / `m` / `Live now`, not
 * Arabic. That is a deliberate exception, not an untranslated key, and this is
 * the only place it can be written down: the locale files are strict JSON and
 * cannot hold a comment (and a `__comment` key would be purged by
 * `i18next-scanner`'s `removeUnusedKeys`).
 *
 * Why: this card ALREADY prints Western-Arabic digits unconditionally (`pad()`
 * in `CongressCard`, `String(n).padStart` — never localised numerals), and
 * `document.dir` follows `activeLang` (`LangSync`), so an Arabic reader whose
 * INTERFACE is not Arabic would get RTL words sitting inside an LTR container
 * next to LTR digits. A half-Arabic card reads worse than an English one. The
 * proper fix is the numerals + direction exception as a whole, which is out of
 * scope here and listed as a follow-up. Arabic needs native validation before
 * these four values become Arabic.
 */
export function resolveCongressCountdownCopy({
  contentLang,
  t,
}: {
  contentLang: SupportedLanguage;
  t: (key: string, options: { lng: string }) => string;
}): CongressCountdownCopy {
  return {
    daysUnit: t("congressCountdown.days", { lng: contentLang }),
    hoursUnit: t("congressCountdown.hours", { lng: contentLang }),
    minutesUnit: t("congressCountdown.minutes", { lng: contentLang }),
    liveNow: t("congressCountdown.liveNow", { lng: contentLang }),
  };
}
